<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold"
        >Business Admins ({{ listMeta.total }})</v-toolbar-title
      >

      <v-spacer></v-spacer>
      <v-btn
        v-show="!loading && isFiltered"
        rounded
        color="error lighten-1"
        class="mr-6"
        @click="clearFilters"
      >
        <span class="white--text mb-1">Clear Filter</span>
        <v-icon right>
          {{ icons.cancel }}
        </v-icon>
      </v-btn>
      <v-row v-show="showSearch" align="center" style="max-width: 370px;">
        <v-text-field
          v-if="!loading"
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search name, email or company"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn class="ml-5" icon @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              color="#383d3f"
              class="mr-1"
              icon
              @click="exportList"
              v-on="on"
            >
              <v-icon>{{ icons.export }}</v-icon>
            </v-btn>
          </template>
          <span>Export to Excel</span>
        </v-tooltip>
      </template>
    </v-app-bar>

    <div
      class="mt-12 mx-4"
      id="user-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
    >
      <v-simple-table>
        <thead>
          <tr>
            <th width="20%" colspan="2" class="text-left pa-0">
              <v-menu offset-y>
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    Name
                    <v-icon>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-list :min-width="230">
                  <v-radio-group v-model="sort" hide-details class="mt-0 py-0">
                    <div v-for="(item, key) in fullNameSort" :key="key">
                      <v-list-item inactive>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-action @click="updateSort(item.value)">
                          <v-radio :value="item.value" />
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="key !== fullNameSort.length - 1" />
                    </div>
                  </v-radio-group>
                </v-list>
              </v-menu>
            </th>
            <th width="20%" class="text-left">Email</th>
            <th width="20%" class="text-left">Company</th>
            <th width="20%" class="text-left">USI Number</th>
            <th width="20%" class="text-left">
              <v-menu
                v-model="datePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    Joined Date
                    <v-icon>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-date-picker
                  v-model="selectedDate"
                  @change="filterDateBusinessAdmins"
                ></v-date-picker>
              </v-menu>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="admin in admins"
            :key="admin.id"
            class="clickable"
            @click="
              $router.push({
                name: 'business-admin.details',
                params: { id: admin.id }
              })
            "
          >
            <td colspan="2" class="text-left text-no-wrap pl-0">
              <user-avatar :avatar="admin.avatar" size="40" class="mr-6" />
              {{ admin.full_name }}
            </td>
            <td>{{ admin.email }}</td>
            <td>{{ admin.company }}</td>
            <td>{{ admin.usi ? admin.usi : 'N/A' }}</td>
            <td>{{ admin.addedDate }}</td>
          </tr>

          <template v-if="loading">
            <tr v-for="index in 10" :key="index + '-skeleton'">
              <td class="py-3 px-0" width="5%">
                <v-skeleton-loader
                  class="avatar-loading--36"
                  type="avatar"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>
      <div
        class="d-flex justify-center mt-3"
        v-if="!(admins.length > 0) && !loading"
      >
        <p>No business-admins found.</p>
      </div>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" right>
      {{ snackbar.message }}
      <v-btn color="white" text @click="snackbar.show = false">Ok</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiMenuDown,
  mdiClose
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import UserAvatar from '@/components/UserAvatar'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'

export default {
  name: 'BusinessAdminPage',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll
  },

  components: {
    UserAvatar,
    AppBarNavIcon
  },

  data() {
    return {
      loading: false,
      showSearch: true,
      showProfile: false,
      selectedProfile: null,
      sort: null,
      filter: {
        search: ''
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        menu: mdiMenuDown,
        cancel: mdiClose
      },
      snackbar: {
        show: false,
        message: null,
        color: null
      },
      dateSort: [
        { value: 'created_at', text: 'Ascending' },
        { value: '-created_at', text: 'Descending' }
      ],
      fullNameSort: [
        { value: 'full_name', text: 'Ascending' },
        { value: '-full_name', text: 'Descending' }
      ],
      datePicker: false,
      selectedDate: null,
      isFiltered: false
    }
  },

  created() {
    this.clearBusinessAdmins()
    this.fetchBusinessAdmins(1)
  },

  computed: {
    ...mapState({
      admins: state => state.businessAdmin.list,
      listMeta: state => state.businessAdmin.listMeta
    }),

    paginationLength() {
      return (this.listMeta.total / this.listMeta.per_page) | 0
    }
  },

  methods: {
    ...mapActions({
      getBusinessAdmins: 'businessAdmin/getBusinessAdmins',
      exportToExcel: 'businessAdmin/exportToExcel'
    }),

    ...mapMutations({
      clearBusinessAdmins: 'businessAdmin/clearBusinessAdminList'
    }),

    pageChanged(page) {
      this.fetchBusinessAdmins(page)
    },

    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchBusinessAdmins(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function() {
      this.clearBusinessAdmins()
      this.fetchBusinessAdmins()
    }, 600),

    async fetchBusinessAdmins(page = 1) {
      if (this.loading) return
      let params = { page }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      if (this.sort) {
        params.sort = this.sort
      }
      if (this.selectedDate) {
        params.searchDate = this.selectedDate
      }
      this.loading = true
      await this.getBusinessAdmins(params)
      this.loading = false
    },

    updateSort() {
      this.isFiltered = true
      this.clearBusinessAdmins()
      this.fetchBusinessAdmins()
    },

    filterDateBusinessAdmins: debounce(function() {
      this.isFiltered = true
      this.datePicker = false
      this.clearBusinessAdmins()
      this.fetchBusinessAdmins()
    }, 600),

    clearFilters() {
      this.sort = null
      this.selectedDate = null
      this.isFiltered = false
      this.clearBusinessAdmins()
      this.fetchBusinessAdmins()
    },

    showProfileDialog(profile) {
      this.selectedProfile = profile
      this.showProfile = true
    },

    async exportList() {
      this.loading = true
      await this.exportToExcel()
        .then(response => {
          const url = URL.createObjectURL(
            new Blob([response.data], {
              type: 'application/vnd.ms-excel'
            })
          )
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'business_admin_list.xlsx')
          document.body.appendChild(link)
          link.click()
          this.showSnackbar('File successfully downloaded', 'success')
        })
        .catch(() => {
          this.showSnackbar('Ops! Something went wrong', 'red')
        })
      this.loading = false
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },
    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    }
  }
}
</script>
